// (C) Copyright 2015 Moodle Pty Ltd.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Injectable } from '@angular/core';
import { WorkplaceTenantsHelper } from '@workplace/features/tenants/services/tenants-helper';
import { FreemiumLoginHelperProvider } from '@freemium/overrides/core/features/login/providers/login-helper';
import { AuthEmailSignupSettings } from '@features/login/services/login-helper';
import { CoreRedirectPayload } from '@services/navigator';
import { CoreWS } from '@services/ws';
import { makeSingleton } from '@singletons';
import { CoreUrl, CoreUrlParams } from '@singletons/url';

/**
 * Override the freemium login helper provider.
 */
@Injectable({ providedIn: 'root' })
export class WorkplaceLoginHelperProvider extends FreemiumLoginHelperProvider {

    /**
     * @inheritdoc
     */
    async getEmailSignupSettings(siteUrl: string): Promise<AuthEmailSignupSettings> {
        const { WorkplaceTenantsHelper } = await import('@workplace/features/tenants/services/tenants-helper');

        const tenanturl = await WorkplaceTenantsHelper.checkTenantUrl(siteUrl);

        return await CoreWS.callAjax(
            'auth_email_get_signup_settings',
            { tenanturl },
            { siteUrl },
        );
    }

    /**
     * @inheritdoc
     */
    async prepareForSSOLogin(
        siteUrl: string,
        service?: string,
        launchUrl?: string,
        redirectData: CoreRedirectPayload = {},
        urlParams?: CoreUrlParams,
    ): Promise<string> {
        const loginUrl = await super.prepareForSSOLogin(siteUrl, service, launchUrl, redirectData, urlParams);

        const tenantId = await WorkplaceTenantsHelper.getTenantId();
        if (!tenantId) {
            return loginUrl;
        }

        return CoreUrl.addParamsToUrl(loginUrl, { tenantid: tenantId });
    }

}
export const WorkplaceLoginHelper = makeSingleton(WorkplaceLoginHelperProvider);
